'use client'

import Stack from '@mui/material/Stack'
import {useSoftwareEvaluationContext} from './SoftwareQualityEvaluationContext'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select, {SelectChangeEvent} from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import {PoFTopicNode, SoftwareEvaluationReportingInformation} from './apiSoftwareIndicator'
import {useEffect, useState} from 'react'
import ListSubheader from '@mui/material/ListSubheader'
import {Check, CheckBox, CheckBoxOutlineBlank, Warning} from '@mui/icons-material'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import Close from '@mui/icons-material/Close'
import {useSoftwareQualityOverview} from '~/components/organisation/reporting/useSoftwareQualityOverview'

export function ReportingInformationCompletionStatus() {
  const evaluationCtx = useSoftwareEvaluationContext()

  return (
    <Stack direction='column' className='gap-2'>
      <strong>Current Completion Status:</strong>
      <Stack direction='row' className='gap-2 items-center flex flex-wrap'>
        {
          evaluationCtx.currentReportingInformation?.map(centre => {
            const centreInformationComplete: boolean = Boolean(centre.contact_person.name && centre.pof_topic.id)
            const centreStatusIcon = centreInformationComplete ? <Check color='secondary'/> : <Close sx={{color: '#ffffff'}}/>
            const centreStatusLabel = <Stack direction='row' className='items-center justify-center gap-1'>{centre.organisation_slug.toUpperCase()}{centreStatusIcon}</Stack>
            return (
              <Chip key={centre.organisation_id} label={centreStatusLabel} color={centreInformationComplete ? 'success' : 'error'} size='small'/>
            )
          }
          )
        }
      </Stack>
    </Stack>
  )
}

export default function SoftwareEvaluationOrganisationReportingInformation() {
  const {currentReportingInformation} = useSoftwareEvaluationContext()

  return (
    <Stack direction="column" spacing={2}>
      <h3>Reporting Information</h3>
      <p>Please provide a PoF Topic and contact person for each Helmholtz centre that is associated with your software. The submitted evaluation can only be considered by centres with complete information.</p>
      <ReportingInformationCompletionStatus />
      {!currentReportingInformation && <Alert severity='warning' variant='outlined'>No Helmholtz organisations are associated with your software. Please assign associated Helmholtz centres in your software entry to use the software quality indicator.</Alert>}
      {
        currentReportingInformation?.map(centre => {
          return (
            <SoftwareEvaluationOrganisationReportingForm key={centre.organisation_id} centre={centre}/>
          )
        }
        )
      }
    </Stack>
  )
}

export function SoftwareEvaluationOrganisationReportingForm({centre}: {centre: SoftwareEvaluationReportingInformation}) {
  const evaluationCtx = useSoftwareEvaluationContext()
  const {pofTopicTrees} = useSoftwareQualityOverview(centre.organisation_id)

  function pofTopicIsInSoftwareMetaData(id: string | null) {
    return id && evaluationCtx.categoriesOfSoftware.has(id)
  }

  return (
    <Stack key={centre.organisation_id} spacing={1}>
      <Stack direction='row' className='justify-between'>
        <a href={`/organisations/${centre.organisation_slug}`}><b>{centre.organisation_name}</b></a>
        { centre.pof_topic.id && !pofTopicIsInSoftwareMetaData(centre.pof_topic.id) && <Stack direction='row' className='text-warning text-sm items-center gap-2'>
          <Warning color='warning'/>
          This PoF Topic is not included in the current software entry.
        </Stack>}
      </Stack>
      <Stack direction='row' className='gap-2 w-full justify-between items-start'>
        <div className='w-1/2'>
          <SoftwareEvaluationContactPersonAutocomplete
            selectedContactPerson={centre.contact_person}
            saveContactPerson={(contact_person: string | null, account?: string) => evaluationCtx.saveContactPerson(centre.organisation_id, contact_person, account)}
          />
        </div>
        <div className='w-1/2'>
          <SoftwareEvaluationPoFTopicDropdown
            key={centre.pof_topic.id}
            tree={evaluationCtx.pofTopics}
            selectedTopic={centre.pof_topic}
            setTopic={(topic_id: string) => (centre.pof_topic.id !== topic_id) ? evaluationCtx.savePoFTopic(centre.organisation_id, topic_id) : null}
            filteredTree={pofTopicTrees.length ? pofTopicTrees : []}
          />
        </div>
      </Stack>
    </Stack>
  )
}

interface BasePoFTopicTreeSelectProps {
  tree: PoFTopicNode[]
  filteredTree?: PoFTopicNode[]
}

interface SinglePoFTopicTreeSelectProps extends BasePoFTopicTreeSelectProps {
  multiple?: false
  selectedTopic: SoftwareEvaluationReportingInformation['pof_topic'] | string
  setTopic: (topic_id: string) => void
}

interface MultiPoFTopicTreeSelectProps extends BasePoFTopicTreeSelectProps {
  multiple: true
  selectedTopic: SoftwareEvaluationReportingInformation['pof_topic'][] | string[]
  setTopic: (topic_ids: string[]) => void
}

export type PoFTopicTreeSelectProps = SinglePoFTopicTreeSelectProps | MultiPoFTopicTreeSelectProps

export function SoftwareEvaluationPoFTopicDropdown(props: PoFTopicTreeSelectProps) {
  const {tree, filteredTree = [], multiple = false, selectedTopic} = props

  const handleChange = (event: SelectChangeEvent<string | string[]>) => {
    const value = event.target.value
    if (multiple) {
      const ids = value as string[]
      const multiProps = props as MultiPoFTopicTreeSelectProps
      multiProps.setTopic(ids as string[])
    } else {
      const singleProps = props as SinglePoFTopicTreeSelectProps
      const singleValue = value as string
      if (singleValue && singleValue) singleProps.setTopic(singleValue)
    }
  }

  const selectItem = (topic: PoFTopicNode, layer: number = 0): React.ReactNode => {
    return topic.children.length ?
      <ListSubheader style={{paddingLeft: 16+(layer*16), marginTop: -5}} key={topic.id}>{topic.name}</ListSubheader>
      : <MenuItem dense style={{paddingLeft: 16+(layer*16), whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4em', paddingTop: 1}} key={topic.id} value={topic.id}>
        {multiple && ((selectedTopic as SoftwareEvaluationReportingInformation['pof_topic'][]).some(st => st.id === topic.id) ? <CheckBox /> : <CheckBoxOutlineBlank />)}
        {topic.name}
      </MenuItem>
  }

  const topicItems = (topic: PoFTopicNode, layer: number = 0) => {
    const menuItems: React.ReactNode[] = []
    menuItems.push(selectItem(topic, layer))
    menuItems.push(topic.children?.sort((a, b) => a.name.localeCompare(b.name)).map((childTopic: PoFTopicNode) => {
      return topicItems(childTopic, layer+1)
    }))
    return menuItems
  }

  const selectedValue = multiple
    ? (selectedTopic as SoftwareEvaluationReportingInformation['pof_topic'][]).map(t => t.id ?? '')
    : (selectedTopic as SoftwareEvaluationReportingInformation['pof_topic']).id!

  return (
    <Stack className='gap-2'>
      <Box minWidth={120}>
        <FormControl fullWidth>
          <InputLabel id="pof-topic-label">PoF Topic</InputLabel>
          <Select
            multiple={multiple}
            labelId="pof-topic-label"
            id="pof-topic-select"
            value={selectedValue ?? ''}
            onChange={handleChange}
            label="PoF Topic"
            MenuProps={{
              slotProps: {
                paper: {
                  sx: {
                    width: '50%',
                    maxWidth: '100%'
                  }
                }
              }
            }}
          >
            {filteredTree.length && <ListSubheader style={{fontWeight: 'bold', fontSize: '1em', color: '#002864'}}>Centre-specific PoF Topics</ListSubheader>}
            {filteredTree.map(topic => {
              return topicItems(topic)
            })}
            <ListSubheader style={{fontWeight: 'bold', fontSize: '1em', color: '#002864', borderTop: filteredTree.length ? '3px solid #14C8FF' : ''}}>Complete PoF Topic List</ListSubheader>
            {
              tree.map(topic => {
                return topicItems(topic)
              })
            }
          </Select>
        </FormControl>
      </Box>
    </Stack>
  )
}

export type EvaluationContactPersonType = 'Maintainer' | 'Contributor'
export type EvaluationContactPersonOption = {
  type: EvaluationContactPersonType
  label: string
  id: string
}

export function SoftwareEvaluationContactPersonAutocomplete({selectedContactPerson, saveContactPerson}: {selectedContactPerson: {id: string | null, name: string | null}, saveContactPerson: (contact_person: string | null, account?: string) => void}) {
  const evaluationCtx = useSoftwareEvaluationContext()
  const [value, setValue] = useState<string>(selectedContactPerson.name || '')
  const [valueChanged, setValueChanged] = useState(false)

  useEffect(() => {
    setValue(selectedContactPerson.name || '')
  }, [selectedContactPerson])

  const updateValue = (newValue: string | undefined) => {
    if (newValue === undefined) return
    setValue(newValue)
    setValueChanged(true)
  }

  const saveValue = () => {
    if (valueChanged) saveContactPerson(value === '' ? null : value)
    setValueChanged(false)
  }


  return (
    <Stack className='gap-2'>
      <Autocomplete
        freeSolo
        options={evaluationCtx.softwareContactPersonOptions}
        groupBy={(option: EvaluationContactPersonOption) => option.type}
        value={selectedContactPerson.name}
        onChange={(ev, val) => {
          if (typeof val === 'string') {
            updateValue(val)
          } else {
            saveContactPerson(val?.label ?? null, val?.id ?? undefined)
            setValueChanged(false)
          }
        }}
        renderInput={(params) =>
          <TextField
            {...params}
            label="Contact Person"
            onBlur={() => saveValue()}
            onChange={(ev) => updateValue(ev.target.value)}
          />
        }
      />
    </Stack>
  )
}

